import React, { useState } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, Bell, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useInstructor } from '@/features/profile/hooks/useInstructor';
import { useInstructorPendingRequestsCount, useInstructorRequests, useApproveClassRequest, useDenyClassRequest } from '@/features/calendar/hooks/useClassRequests';
import { RequestManagementSection } from '@/components/classRequests/RequestManagementSection';
import { AgendaConfigurationModal } from '@/features/calendar/components/AgendaConfigurationModal';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const RequestForClassesSection: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isInstructor, isLoading: instructorLoading } = useInstructor();
  const [isAgendaModalOpen, setIsAgendaModalOpen] = useState(false);

  // Fetch current user's username for slot navigation
  const { data: currentUsername = '' } = useQuery({
    queryKey: ['profile-username', user?.id],
    queryFn: async () => {
      if (!user?.id) return '';
      const { data } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();
      return data?.username ?? '';
    },
    enabled: !!user?.id,
  });

  // Get pending requests count and data
  const { data: pendingCount = 0, isLoading: countLoading } = useInstructorPendingRequestsCount(user?.id || '');
  const { data: requests = [], isLoading: requestsLoading } = useInstructorRequests(user?.id || '');

  // Mutation hooks
  const approveMutation = useApproveClassRequest();
  const denyMutation = useDenyClassRequest();

  const handleSetUpSchedule = () => {
    setIsAgendaModalOpen(true);
  };

  const handleAgendaConfigSaved = () => {
    setIsAgendaModalOpen(false);
  };

  const handleSeeAll = () => {
    navigate(`/instructor-slots/${currentUsername}`);
  };

  const handleApprove = async (requestId: string) => {
    await approveMutation.mutateAsync({ requestId, instructorId: user?.id || '' });
  };

  const handleDeny = async (requestId: string) => {
    await denyMutation.mutateAsync({ requestId, instructorId: user?.id || '' });
  };

  const handleViewProfile = (studentId: string) => {
    navigate(`/profile/${studentId}`);
  };

  // Don't show section if user is not an instructor or still loading
  if (!isInstructor || instructorLoading) {
    return null;
  }

  // Show management section if there are requests, otherwise show empty state
  return (
    <>
      {pendingCount > 0 && !countLoading ? (
        <RequestManagementSection
          instructorId={user?.id || ''}
          instructorUsername={currentUsername}
          requests={requests}
          pendingCount={pendingCount}
          isLoading={countLoading || requestsLoading}
          onApprove={handleApprove}
          onDeny={handleDeny}
          onViewProfile={handleViewProfile}
        />
      ) : (
        /* Empty state - fontes pequenas, botão altura reduzida */
        <div className="bg-[#2D2424] rounded-lg p-4 text-center mb-4">
          <h3 className="text-white font-bold text-xs mb-1 font-roboto">
            {t('home.instructor_mode.share_free_spots')}
          </h3>
          <p className="text-white/80 text-[10px] mb-3 font-roboto">
            {t('home.instructor_mode.make_schedule_public')}
          </p>
          
          <button
            onClick={handleSeeAll}
            className="border border-white text-white hover:bg-white/10 px-4 py-1 rounded-full text-[10px] font-medium transition-colors"
          >
            {t('home.instructor_mode.go_to_class_slots')}
          </button>
        </div>
      )}
      
      {/* Agenda Configuration Modal */}
      <AgendaConfigurationModal
        isOpen={isAgendaModalOpen}
        onClose={() => setIsAgendaModalOpen(false)}
        onConfigSaved={handleAgendaConfigSaved}
      />
    </>
  );
};

export default RequestForClassesSection;



